import React, { useState, useEffect } from 'react';
import './CounselMainHeader.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { recoilCustomerInfoFormData, recoilCityCart } from '../../../RecoilStore';
import logoImage from '../../images/counsel/logo.png';
import CounselMenuModal from './CounselMenuModal';
import axios from 'axios';
import { AdminURL } from '../../../MainURL';

const CounselTourHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const customerInfoFormData = useRecoilValue(recoilCustomerInfoFormData);
  const cityCart = useRecoilValue(recoilCityCart);
  const setCityCart = useSetRecoilState(recoilCityCart);

  const toggleMenu = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);
    // 메뉴가 열릴 때 body 스크롤 막기
    if (newMenuState) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = '';
  };

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        closeMenu();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);


  const isActiveMenu = (path: string) => {
    if (path === '/counsel/europe') {
      // 여행지 경로: /counsel/europe만 정확히 일치할 때 활성화 (EuropeTripPage.tsx)
      return location.pathname === '/counsel/europe' || location.pathname === '/counsel/europe/';
    } else if (path === '/counsel/europe/city') {
      // 도시 경로: /counsel/europe/city 또는 /counsel/europe/citydetail로 시작하는 경우 (EuropeCityPage.tsx, EuropeCityDetail.tsx)
      return location.pathname.startsWith('/counsel/europe/city');
    } else if (path === '/counsel/europe/schedule') {
      // 일정 경로: /counsel/europe/schedule로 시작하는 경우 (EuropeScheduleCost.tsx)
      return location.pathname.startsWith('/counsel/europe/schedule');
    } else if (path === '/counsel/europe/estimate') {
      // 견적 경로: /counsel/europe/estimate로 시작하는 경우
      return location.pathname.startsWith('/counsel/europe/estimate');
    }
    return location.pathname.startsWith(path);
  };

  const formatTravelInfo = () => {
    const customer1 = customerInfoFormData.customer1Name;
    const customer2 = customerInfoFormData.customer2Name;
    const travelPeriodStart = customerInfoFormData.travelPeriodStart;
    const travelPeriodEnd = customerInfoFormData.travelPeriodEnd;
    
    if (!customer1 && !customer2 && !travelPeriodStart && !travelPeriodEnd) {
      return '';
    }
    
    // 고객명 처리 (둘 다 있으면 ", "로 구분, 하나만 있으면 그대로 사용)
    let customerText = '';
    if (customer1 && customer2) {
      customerText = `${customer1}, ${customer2}`;
    } else if (customer1) {
      customerText = customer1;
    } else if (customer2) {
      customerText = customer2;
    }
    
    // 여행기간이 있으면 고객명과 함께 표시, 없으면 고객명만 표시
    if (travelPeriodStart && travelPeriodEnd) {
      return customerText ? `${customerText}, ${travelPeriodStart} ~ ${travelPeriodEnd}` : `${travelPeriodStart} ~ ${travelPeriodEnd}`;
    }
    
    return customerText;
  };

  // GO 버튼 클릭 시 EuropeCityDetail로 이동 (첫 번째 도시)
  const handleScheduleEdit = () => {
    if (cityCart.length === 0) {
      alert('장바구니에 도시가 없습니다.');
      return;
    }
    // 첫 번째 도시의 ID와 nation을 사용하여 EuropeCityDetail로 이동
    const firstCity = cityCart[0];
    navigate(`/counsel/europe/citydetail?id=${firstCity.id}&nation=${firstCity.nation || ''}&fromGo=true`);
    window.scrollTo(0, 0);
  };

  // 네비게이션 메뉴 항목
  const navMenuItems = [
    { id: 'trip', name: '여행지', path: '/counsel/europe' },
    { id: 'city', name: '도시', path: '/counsel/europe/city' },
    { id: 'schedule', name: '일정', path: '/counsel/europe/schedule' },
    { id: 'estimate', name: '견적', path: '/counsel/europe/estimate' },
  ];

  return (
    <div className="counsel-header">
      <div className="header-container">

        <div className="header-left">
          {/* 로고 */}
          <div className="header-logo" onClick={() => navigate('/counsel')}>
            <img src={logoImage} alt="CEYLON TOUR" />
          </div>

          <div className="header-info-wrapper">
            {/* 여행 정보 표시 */}
            {(customerInfoFormData.customer1Name || customerInfoFormData.customer2Name || customerInfoFormData.travelPeriodStart || customerInfoFormData.travelPeriodEnd) && (
              <div className="header-travel-info">
                {formatTravelInfo()}
              </div>
            )}

            {/* 도시 장바구니 표시 */}
            {cityCart.length > 0 && (() => {
              return (
                <div className="header-city-cart" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: '0' }}>
                <span style={{ fontSize: '14px', color: '#333' }}>
                  담은 도시: {cityCart.length}개
                </span>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {cityCart.map((item) => {
                    return (
                      <div
                        key={item.id}
                        onClick={() => {
                          const isAlreadyOnDetailPage = location.pathname.startsWith('/counsel/europe/citydetail');
                          navigate(`/counsel/europe/citydetail?id=${item.id}&nation=${item.nation || ''}`, {
                            replace: isAlreadyOnDetailPage
                          });
                          window.scrollTo(0, 0);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          backgroundColor: '#f0f0f0',
                          color: '#333',
                          borderRadius: '4px',
                          fontSize: '16px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          border: 'none',
                          fontWeight: '400'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e0e0e0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0f0f0';
                        }}
                      >
                        <span>{item.cityKo}</span>
                        {item.nation && <span style={{ color: '#666' }}>({item.nation})</span>}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCityCart((prev) => prev.filter((city) => city.id !== item.id));
                          }}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: '0',
                            marginLeft: '4px',
                            fontSize: '14px',
                            color: '#999'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={handleScheduleEdit}
                  style={{
                    padding: '6px 16px',
                    borderRadius: '6px',
                    border: '1px solid #333',
                    color: '#333',
                    fontSize: '14px',
                    fontWeight: '500',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#555';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.color = '#333';
                  }}
                >
                  GO
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('담은 도시를 모두 삭제하시겠습니까?')) {
                      setCityCart([]);
                    }
                  }}
                  style={{
                    padding: '6px 16px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    color: '#666',
                    fontSize: '14px',
                    fontWeight: '500',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#999';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.borderColor = '#ddd';
                  }}
                >
                  초기화
                </button>
              </div>
            );
          })()}
          </div>
        </div>

        {/* 네비게이션 메뉴 */}
        <nav className="header-nav-menu">
          {navMenuItems.map((item) => (
            <div
              key={item.id}
              className={`nav-menu-item ${isActiveMenu(item.path) ? 'active' : ''}`}
              onClick={() => {
                // navigate(item.path)
              }}
            >
              {item.name}
            </div>
          ))}
        </nav>

        {/* 햄버거버튼 */}
        <button 
          className={`header-hamburger ${isMenuOpen ? 'active' : ''}`} 
          onClick={toggleMenu}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
       
      </div>

      {/* 전체 화면 모달 */}
      <CounselMenuModal
        isOpen={isMenuOpen}
        onClose={closeMenu}
      />
    </div>
  );
};

export default CounselTourHeader;
